import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';
import { createColumnHelper, flexRender, getCoreRowModel, SortingState, useReactTable } from '@tanstack/react-table';

import { Dimensions, Metrics, Sources } from 'components/insights/constants';
import {
  DataTableTheme,
  Table as StyledTable,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderFooterRow,
  TableRow,
  TableSortIcon,
} from 'components/shared/DataTable';
import TablePaginator from 'components/shared/table/TablePaginator';
import usePagination from 'hooks/usePagination';
import { simpleDate } from 'lib/date';
import { retrieveAnalytics } from 'services/api/insights/analytics';
import { serializeSort } from 'stores/helpers';
import {
  getReferralAnalyticsFilters,
  getSlideoutEntityFilter,
  useReferralsInsightsStore,
} from 'stores/referralsInsightsStore';
import ArrowUpDownIcon from 'svg/ArrowUpDownIcon';

type ReferralRow = {
  reviewId: string;
  episodeId: string;
  patientName: string;
  referredBy: string;
  referredTo: string;
  createdAt: string;
};

const columnHelper = createColumnHelper<ReferralRow>();

const columns = [
  columnHelper.accessor('patientName', {
    id: Dimensions.PATIENT_NAME,
    header: 'Patient Name',
    cell: (info) => {
      const name = info.getValue();
      const { episodeId, reviewId } = info.row.original;
      if (!name) return '—';
      if (!episodeId || !reviewId) return name;
      // Deep link into the patient story at the specific referral.
      return (
        <ReferralLink href={`/patients/${episodeId}/referrals/${reviewId}`} target='_blank' rel='noopener noreferrer'>
          {name}
        </ReferralLink>
      );
    },
    enableSorting: true,
  }),
  columnHelper.accessor('referredBy', {
    id: Dimensions.SENDING_GROUP,
    header: 'Referred By',
    cell: (info) => info.getValue() || '—',
    enableSorting: true,
  }),
  columnHelper.accessor('referredTo', {
    id: Dimensions.RECEIVING_GROUP,
    header: 'Referred To',
    cell: (info) => info.getValue() || '—',
    enableSorting: true,
  }),
  columnHelper.accessor('createdAt', {
    id: Dimensions.DATE,
    header: 'Referral Created Date',
    cell: (info) => (info.getValue() ? simpleDate(info.getValue()) : '—'),
    enableSorting: true,
  }),
];

const QUERY_DIMENSIONS = [
  Dimensions.REVIEW_ID,
  Dimensions.EPISODE_ID,
  Dimensions.PATIENT_NAME,
  `${Dimensions.SENDING_GROUP}:with_id`,
  `${Dimensions.RECEIVING_GROUP}:with_id`,
  Dimensions.DATE,
];

// review_id:count is always 1 at this grain — the analytics endpoint requires at least one metric.
const QUERY_METRICS = [Metrics.REVIEW_ID_COUNT];

const METRIC_STATUS_FILTER: Record<string, string | undefined> = {
  denial_rate: 'rejected',
  acceptance_rate: 'accepted',
  response_time: 'pending',
};

export default function PatientListSection() {
  const baseFilters = useReferralsInsightsStore((state) => getReferralAnalyticsFilters(state));
  const slideoutContext = useReferralsInsightsStore((state) => state.slideoutContext);

  const analyticsFilters = useMemo(() => {
    if (!baseFilters) return null;
    const statusFilter = slideoutContext?.kind === 'metric' ? METRIC_STATUS_FILTER[slideoutContext.metric] : undefined;
    // For entity drills, lock results to the clicked entity (same merge the rest of the slideout uses).
    const entityFilter = slideoutContext ? getSlideoutEntityFilter(slideoutContext) : null;
    return {
      ...baseFilters,
      ...(entityFilter ?? {}),
      ...(statusFilter ? { referralStatus: statusFilter } : {}),
    };
  }, [baseFilters, slideoutContext]);

  const pagination = usePagination({ pageSize: 10 });
  const [sort, setSort] = useState<SortingState>([{ id: Dimensions.DATE, desc: true }]);

  const queryParams = useMemo(
    () => ({
      source: Sources.INSIGHTS_REFERRALS,
      dimensions: QUERY_DIMENSIONS,
      metrics: QUERY_METRICS,
      sortBy: serializeSort(sort),
      page: pagination.paging.page,
      pageSize: pagination.paging.pageSize,
    }),
    [sort, pagination.paging]
  );

  const { data, isLoading } = useQuery({
    queryKey: ['referral-slideout-patient-list', queryParams, analyticsFilters],
    queryFn: () => retrieveAnalytics(queryParams, analyticsFilters),
    enabled: !!analyticsFilters,
  });

  // Internal dim columns for [review_id, episode_id, patient_name, sending_group:with_id, receiving_group:with_id, date]:
  // 0 = review_id, 1 = episode_id, 2 = patient_name, 3 = sending_group_name, 4 = sending_group_id,
  // 5 = receiving_group_name, 6 = receiving_group_id, 7 = date
  const rows = useMemo<ReferralRow[]>(() => {
    const responseRows = data?.data ?? [];
    return responseRows.map((row): ReferralRow => {
      const dimValue = (idx: number) => row.dimensionValues[idx]?.value;
      return {
        reviewId: String(dimValue(0) ?? ''),
        episodeId: String(dimValue(1) ?? ''),
        patientName: String(dimValue(2) ?? ''),
        referredBy: String(dimValue(3) ?? ''),
        referredTo: String(dimValue(5) ?? ''),
        createdAt: String(dimValue(7) ?? ''),
      };
    });
  }, [data]);

  // The analytics endpoint uses Kaminari's `without_count` (aliased GROUP BY breaks the count
  // query), so total record/page counts aren't sent. Page count is inferred from the next link.
  const hasNextPage = !!data?.links?.next?.url;
  const totalPages = hasNextPage ? pagination.paging.page + 1 : pagination.paging.page;

  const table = useReactTable({
    columns,
    data: rows,
    state: { sorting: sort },
    onSortingChange: (updater) => {
      const next = updater instanceof Function ? updater(sort) : updater;
      setSort(next);
      pagination.setPageIndex(0);
    },
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Wrapper>
      <Header>
        <Title>Referrals</Title>
      </Header>
      <StyledTable $theme={DataTableTheme.DEFAULT} $maxHeight='none'>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableHeaderFooterRow key={headerGroup.id} $theme={DataTableTheme.DEFAULT}>
              {headerGroup.headers.map((header) => (
                <TableHeaderCell
                  key={header.id}
                  isSorted={!!header.column.getIsSorted()}
                  isSortable={header.column.getCanSort()}
                  onClick={header.column.getToggleSortingHandler()}
                  width={header.getSize()}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {header.column.getCanSort() && (
                    <TableSortIcon
                      as={ArrowUpDownIcon}
                      direction={header.column.getIsSorted() || 'none'}
                      height={10}
                      width={10}
                    />
                  )}
                </TableHeaderCell>
              ))}
            </TableHeaderFooterRow>
          ))}
        </TableHeader>
        <TableBody>
          {rows.length === 0 && !isLoading && (
            <TableRow>
              <EmptyCell>No referrals found for this period.</EmptyCell>
            </TableRow>
          )}
          {rows.length > 0 &&
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} width={cell.column.getSize()}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
        </TableBody>
      </StyledTable>
      <TablePaginator
        canNextPage={pagination.paging.page < totalPages}
        canPreviousPage={pagination.paging.page > 1}
        gotoPage={pagination.setPageIndex}
        nextPage={() => pagination.setPageIndex(pagination.paging.page)}
        previousPage={() => pagination.setPageIndex(pagination.paging.page - 2)}
        onPageChange={pagination.setPageIndex}
        pageCount={totalPages}
        pageIndex={pagination.paging.page - 1}
      />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

const Title = styled.h3`
  font-size: 14px;
  font-weight: 700;
  margin: 0;
  color: ${({ theme }) => theme.colors.black};
`;

const ReferralLink = styled.a`
  color: ${({ theme }) => theme.colors.primaryBlue};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const EmptyCell = styled.td`
  padding: 24px;
  width: 100%;
  text-align: center;
  color: ${({ theme }) => theme.colors.black50};
`;
